/**
 * Firebase Products Service
 * Handles Firestore product operations and image upload to Storage
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  DocumentSnapshot,
  QueryConstraint,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { db, storage, COLLECTIONS } from '@/config/firebase.config';
import { Product, ProductFilters, ProductFormData, ProductCategory } from '@/types/product.types';
import { SIZE_MATRIX_CONFIGS } from '@/data/sizeMatrixConfigs';

/**
 * Resilient fallback: if a product is configured for a size grid in
 * SIZE_MATRIX_CONFIGS (keyed by slug) but its Firestore doc hasn't been migrated
 * yet, attach the config (and matching sizes) at read time. Once the doc carries
 * its own sizeMatrixConfig, the stored value wins. Keeps the selector working
 * before the admin migration runs, with no data drift (single shared source).
 */
const hydrateSizeMatrix = (product: Product): Product => {
  const seed = SIZE_MATRIX_CONFIGS[product.slug];
  if (!seed || product.sizeMatrixConfig) return product;
  return {
    ...product,
    sizeMatrixConfig: seed.sizeMatrixConfig,
    sizes: seed.sizes,
  };
};

/**
 * Generate a URL-friendly slug from product name
 */
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Generate a unique ID
 */
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Calculate discount percentage
 */
const calculateDiscount = (price: number, originalPrice?: number): number => {
  if (!originalPrice || originalPrice <= price) return 0;
  return Math.round(((originalPrice - price) / originalPrice) * 100);
};

/**
 * Get all products with optional filters
 */
export const getAllProducts = async (
  filters?: ProductFilters,
  pageSize: number = 50,
  lastDoc?: DocumentSnapshot
): Promise<{ products: Product[]; lastDoc: DocumentSnapshot | null }> => {
  try {
    const constraints: QueryConstraint[] = [];

    // Apply filters
    if (filters?.category) {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters?.brand) {
      constraints.push(where('brand', '==', filters.brand));
    }
    if (filters?.inStock !== undefined) {
      constraints.push(where('inStock', '==', filters.inStock));
    }
    if (filters?.isFeatured !== undefined) {
      constraints.push(where('isFeatured', '==', filters.isFeatured));
    }

    // Apply sorting
    switch (filters?.sortBy) {
      case 'price_asc':
        constraints.push(orderBy('price', 'asc'));
        break;
      case 'price_desc':
        constraints.push(orderBy('price', 'desc'));
        break;
      case 'name_asc':
        constraints.push(orderBy('nameLowercase', 'asc'));
        break;
      case 'name_desc':
        constraints.push(orderBy('nameLowercase', 'desc'));
        break;
      case 'newest':
        constraints.push(orderBy('createdAt', 'desc'));
        break;
      default:
        constraints.push(orderBy('createdAt', 'desc'));
    }

    // Pagination
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }
    constraints.push(limit(pageSize));

    const q = query(collection(db, COLLECTIONS.PRODUCTS), ...constraints);
    const snapshot = await getDocs(q);

    const products: Product[] = snapshot.docs.map(doc =>
      hydrateSizeMatrix({ id: doc.id, ...doc.data() } as Product)
    );

    // Client-side search filter (Firestore doesn't support full-text search)
    let filteredProducts = products;
    if (filters?.search) {
      const searchLower = filters.search.toLowerCase();
      filteredProducts = products.filter(p =>
        p.name.toLowerCase().includes(searchLower) ||
        p.description.toLowerCase().includes(searchLower) ||
        p.sku.toLowerCase().includes(searchLower)
      );
    }

    // Client-side price range filter
    if (filters?.minPrice !== undefined) {
      filteredProducts = filteredProducts.filter(p => p.price >= filters.minPrice!);
    }
    if (filters?.maxPrice !== undefined) {
      filteredProducts = filteredProducts.filter(p => p.price <= filters.maxPrice!);
    }

    const lastVisible = snapshot.docs.length > 0 
      ? snapshot.docs[snapshot.docs.length - 1] 
      : null;

    return { products: filteredProducts, lastDoc: lastVisible };
  } catch (error) {
    console.error('Error fetching products:', error);
    throw new Error('Failed to fetch products');
  }
};

/**
 * Get multiple products by their IDs (batched, max 30 per Firestore `in` query)
 */
export const getProductsByIds = async (productIds: string[]): Promise<Product[]> => {
  if (productIds.length === 0) return [];

  try {
    const results: Product[] = [];
    const chunks: string[][] = [];
    for (let i = 0; i < productIds.length; i += 30) {
      chunks.push(productIds.slice(i, i + 30));
    }

    for (const chunk of chunks) {
      const q = query(
        collection(db, COLLECTIONS.PRODUCTS),
        where('__name__', 'in', chunk)
      );
      const snapshot = await getDocs(q);
      snapshot.docs.forEach(d => {
        results.push(hydrateSizeMatrix({ id: d.id, ...d.data() } as Product));
      });
    }

    return results;
  } catch (error) {
    console.error('Error fetching products by IDs:', error);
    return [];
  }
};

/**
 * Get product by ID
 */
export const getProductById = async (productId: string): Promise<Product | null> => {
  try {
    const productDoc = await getDoc(doc(db, COLLECTIONS.PRODUCTS, productId));

    if (!productDoc.exists()) {
      return null;
    }

    return hydrateSizeMatrix({
      id: productDoc.id,
      ...productDoc.data(),
    } as Product);
  } catch (error) {
    console.error('Error fetching product:', error);
    throw new Error('Failed to fetch product');
  }
};

/**
 * Get product by slug
 */
export const getProductBySlug = async (slug: string): Promise<Product | null> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where('slug', '==', slug),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return hydrateSizeMatrix({
      id: doc.id,
      ...doc.data(),
    } as Product);
  } catch (error) {
    console.error('Error fetching product by slug:', error);
    throw new Error('Failed to fetch product');
  }
};

/**
 * Upload product image to Firebase Storage
 * When nameParts is provided, filename follows: ProductName_Category_Brand_index.ext
 */
export const uploadProductImage = async (
  file: File,
  nameParts?: { name: string; category: string; brand: string; index: number }
): Promise<string> => {
  try {
    const ext = file.name.split('.').pop() || 'png';
    let fileName: string;
    if (nameParts) {
      const safeName = nameParts.name.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').toLowerCase();
      const safeCat = nameParts.category.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').toLowerCase();
      const safeBrand = nameParts.brand.replace(/[^a-zA-Z0-9]/g, '-').replace(/-+/g, '-').toLowerCase();
      fileName = `${safeName}_${safeCat}_${safeBrand}_${nameParts.index}.${ext}`;
    } else {
      fileName = `${generateId()}_${Date.now()}_${file.name}`;
    }
    const storageRef = ref(storage, `products/${fileName}`);

    await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(storageRef);

    return downloadUrl;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error('Failed to upload image');
  }
};

/**
 * Delete product image from Firebase Storage
 */
export const deleteProductImage = async (imageUrl: string): Promise<void> => {
  try {
    // Extract path from URL
    const storageRef = ref(storage, imageUrl);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting image:', error);
    // Don't throw - image might already be deleted
  }
};

/**
 * Create new product
 */
export const createProduct = async (
  formData: ProductFormData
): Promise<Product> => {
  try {
    const now = Timestamp.now();
    const slug = generateSlug(formData.name);

    // Upload images if they are File objects
    const imageUrls: string[] = [];
    for (let i = 0; i < formData.images.length; i++) {
      const image = formData.images[i];
      if (image instanceof File) {
        const url = await uploadProductImage(image, {
          name: formData.name,
          category: formData.category,
          brand: formData.brand,
          index: i + 1,
        });
        imageUrls.push(url);
      } else {
        imageUrls.push(image);
      }
    }

    const productData: Omit<Product, 'id'> = {
      name: formData.name,
      nameLowercase: formData.name.toLowerCase(),
      slug,
      description: formData.description,
      shortDescription: formData.shortDescription || '',
      price: formData.price,
      originalPrice: formData.originalPrice || null as unknown as number,
      discount: calculateDiscount(formData.price, formData.originalPrice),
      category: formData.category,
      brand: formData.brand,
      images: imageUrls,
      thumbnail: imageUrls[0] || '',
      specifications: formData.specifications,
      moq: formData.moq || 1,
      inStock: formData.stockQuantity > 0,
      stockQuantity: formData.stockQuantity,
      colors: formData.colors || [],
      sizes: formData.sizes || [],
      sku: formData.sku,
      isFeatured: formData.isFeatured,
      isActive: formData.isActive,
      tags: formData.tags || [],
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);

    return {
      id: docRef.id,
      ...productData,
    };
  } catch (error) {
    console.error('Error creating product:', error);
    throw new Error('Failed to create product');
  }
};

/**
 * Update existing product
 */
export const updateProduct = async (
  productId: string,
  formData: Partial<ProductFormData>
): Promise<void> => {
  try {
    // Using Partial<Product> for proper Firestore typing
    const updates: Partial<Product> & { updatedAt: Timestamp } = {
      updatedAt: Timestamp.now(),
    };

    // Handle each field
    if (formData.name !== undefined) {
      updates.name = formData.name;
      updates.nameLowercase = formData.name.toLowerCase();
      updates.slug = generateSlug(formData.name);
    }
    if (formData.description !== undefined) {
      updates.description = formData.description;
    }
    if (formData.shortDescription !== undefined) {
      updates.shortDescription = formData.shortDescription;
    }
    if (formData.price !== undefined) {
      updates.price = formData.price;
      updates.discount = calculateDiscount(formData.price, formData.originalPrice);
    }
    if (formData.originalPrice !== undefined) {
      updates.originalPrice = formData.originalPrice;
      if (formData.price !== undefined) {
        updates.discount = calculateDiscount(formData.price, formData.originalPrice);
      }
    }
    if (formData.category !== undefined) {
      updates.category = formData.category;
    }
    if (formData.brand !== undefined) {
      updates.brand = formData.brand;
    }
    if (formData.specifications !== undefined) {
      updates.specifications = formData.specifications;
    }
    if (formData.moq !== undefined) {
      updates.moq = formData.moq;
    }
    if (formData.stockQuantity !== undefined) {
      updates.stockQuantity = formData.stockQuantity;
      updates.inStock = formData.stockQuantity > 0;
    }
    if (formData.colors !== undefined) {
      updates.colors = formData.colors;
    }
    if (formData.sizes !== undefined) {
      updates.sizes = formData.sizes;
    }
    if (formData.sku !== undefined) {
      updates.sku = formData.sku;
    }
    if (formData.isFeatured !== undefined) {
      updates.isFeatured = formData.isFeatured;
    }
    if (formData.isActive !== undefined) {
      updates.isActive = formData.isActive;
    }
    if (formData.tags !== undefined) {
      updates.tags = formData.tags;
    }

    // Handle images
    if (formData.images !== undefined) {
      const existingProduct = await getProductById(productId);
      const productName = formData.name || existingProduct?.name || 'product';
      const productCat = formData.category || existingProduct?.category || 'misc';
      const productBrand = formData.brand || existingProduct?.brand || 'unknown';

      const imageUrls: string[] = [];
      for (let i = 0; i < formData.images.length; i++) {
        const image = formData.images[i];
        if (image instanceof File) {
          const url = await uploadProductImage(image, {
            name: productName,
            category: productCat,
            brand: productBrand,
            index: i + 1,
          });
          imageUrls.push(url);
        } else {
          imageUrls.push(image);
        }
      }
      updates.images = imageUrls;
      updates.thumbnail = imageUrls[0] || '';
    }

    await updateDoc(doc(db, COLLECTIONS.PRODUCTS, productId), updates);
  } catch (error) {
    console.error('Error updating product:', error);
    throw new Error('Failed to update product');
  }
};

/**
 * Delete product
 */
export const deleteProduct = async (productId: string): Promise<void> => {
  try {
    // Get product to delete images
    const product = await getProductById(productId);

    if (product) {
      // Delete all product images from storage
      for (const imageUrl of product.images) {
        await deleteProductImage(imageUrl);
      }
    }

    // Delete product document
    await deleteDoc(doc(db, COLLECTIONS.PRODUCTS, productId));
  } catch (error) {
    console.error('Error deleting product:', error);
    throw new Error('Failed to delete product');
  }
};

/**
 * Get featured products
 * Uses single-field query with client-side filtering to avoid composite index requirement
 */
export const getFeaturedProducts = async (limitCount: number = 8): Promise<Product[]> => {
  try {
    // Simple query without composite index requirement
    const q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where('isFeatured', '==', true)
    );
    const snapshot = await getDocs(q);
    
    // Client-side filtering for isActive and sorting
    const products = snapshot.docs
      .map(doc => hydrateSizeMatrix({ id: doc.id, ...doc.data() } as Product));
    
    return products
      .filter(p => p.isActive)
      .sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      })
      .slice(0, limitCount);
  } catch (error) {
    console.error('Error fetching featured products:', error);
    throw new Error('Failed to fetch featured products');
  }
};

/**
 * Get products by category
 */
export const getProductsByCategory = async (
  category: ProductCategory,
  limitCount: number = 20
): Promise<Product[]> => {
  try {
    const q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where('category', '==', category),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc =>
      hydrateSizeMatrix({ id: doc.id, ...doc.data() } as Product)
    );
  } catch (error) {
    console.error('Error fetching products by category:', error);
    throw new Error('Failed to fetch products');
  }
};

/**
 * Get products count
 */
export const getProductsCount = async (): Promise<number> => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.PRODUCTS));
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching products count:', error);
    return 0;
  }
};

/**
 * Migrate a single product (for migration script)
 */
export const migrateProduct = async (product: Product): Promise<string> => {
  try {
    const productData: Omit<Product, 'id'> = {
      ...product,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    delete (productData as Record<string, unknown>).id;

    const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);
    return docRef.id;
  } catch (error) {
    console.error('Error migrating product:', error);
    throw new Error(`Failed to migrate product: ${product.name}`);
  }
};

/**
 * Targeted migration: adds crownSizeConfig to the STAR Pedo Crowns product
 * without overwriting any other fields.
 */
export const migrateCrownSizeConfig = async (): Promise<void> => {
  const slug = 'star-stainless-steel-pedo-crowns';
  const q = query(
    collection(db, COLLECTIONS.PRODUCTS),
    where('slug', '==', slug),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) throw new Error('STAR Pedo Crowns product not found in Firestore');

  const productDoc = snapshot.docs[0];
  await updateDoc(doc(db, COLLECTIONS.PRODUCTS, productDoc.id), {
    crownSizeConfig: {
      quadrants: [
        { id: 'UR', label: 'Upper Right', shortLabel: 'UR' },
        { id: 'UL', label: 'Upper Left', shortLabel: 'UL' },
        { id: 'LR', label: 'Lower Right', shortLabel: 'LR' },
        { id: 'LL', label: 'Lower Left', shortLabel: 'LL' },
      ],
      toothTypes: [
        { id: 'D', label: 'Primary First Molar', notation: 'D' },
        { id: 'E', label: 'Primary Second Molar', notation: 'E' },
      ],
      sizes: [2, 3, 4, 5, 6, 7],
    },
    sizes: ['Size 2', 'Size 3', 'Size 4', 'Size 5', 'Size 6', 'Size 7'],
    updatedAt: Timestamp.now(),
  });
};

/**
 * Data-driven migration: pushes the SizeMatrixSelector config + matching sizes onto
 * each product in SIZE_MATRIX_CONFIGS (keyed by slug). Products not found are skipped.
 * Adding a new size-selector product is a single entry in src/data/sizeMatrixConfigs.ts.
 */
export const migrateSizeMatrixConfigs = async (): Promise<{ updated: number; missing: string[] }> => {
  let updated = 0;
  const missing: string[] = [];

  for (const [slug, seed] of Object.entries(SIZE_MATRIX_CONFIGS)) {
    const q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where('slug', '==', slug),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      missing.push(slug);
      continue;
    }

    const productDoc = snapshot.docs[0];
    await updateDoc(doc(db, COLLECTIONS.PRODUCTS, productDoc.id), {
      sizeMatrixConfig: seed.sizeMatrixConfig,
      sizes: seed.sizes,
      updatedAt: Timestamp.now(),
    });
    updated += 1;
  }

  return { updated, missing };
};

/**
 * Migrate local product images to Firebase Storage and update Firestore documents
 */
export const migrateImages = async (
  onProgress?: (msg: string) => void
): Promise<{ uploaded: number; updated: number; errors: number }> => {
  const localImages = [
    '/images/products/disposables/3-ply-mask.png',
    '/images/products/disposables/cotton-rolls.png',
    '/images/products/disposables/suction-tips-1.jpg',
    '/images/products/disposables/suction-tips-2.jpg',
    '/images/products/disposables/suction-tips.png',
    '/images/products/endodontics/dexell-niti-hand-files.png',
    '/images/products/endodontics/dexell-niti-rotary-files.png',
    '/images/products/endodontics/diadent-gutta-percha.png',
    '/images/products/endodontics/mailyard-fiber-post.png',
    '/images/products/endodontics/mani-k-files.png',
    '/images/products/handpieces/hagen-implant-handpiece-2.png',
    '/images/products/handpieces/hagen-implant-handpiece.png',
    '/images/products/handpieces/hagen-mini-head-handpiece.png',
    '/images/products/handpieces/hagen-push-button-handpiece.png',
    '/images/products/handpieces/hagen-straight-handpiece.png',
    '/images/products/handpieces/hagen-surgical-handpiece.png',
    '/images/products/pedodontics/dexell-pedo-rotary-file.png',
    '/images/products/pedodontics/hagen-cordless-endomotor.png',
    '/images/products/pedodontics/pedo-extraction-forceps.png',
    '/images/products/pedodontics/star-pedo-crowns.png',
    '/images/products/sterilization/nexen-autoclave.png',
    '/images/products/sterilization/nexen-uv-chamber.png',
    '/images/products/sterilization/sterilization-roll.png',
  ];

  const pathToUrl = new Map<string, string>();
  let uploaded = 0;
  let errors = 0;

  // Step 1: Upload each image to Firebase Storage
  for (const localPath of localImages) {
    try {
      const response = await fetch(localPath);
      if (!response.ok) {
        console.error(`Failed to fetch ${localPath}: ${response.status}`);
        errors++;
        continue;
      }
      const blob = await response.blob();
      const fileName = localPath.replace('/images/', '');
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, blob);
      const downloadUrl = await getDownloadURL(storageRef);
      pathToUrl.set(localPath, downloadUrl);
      uploaded++;
      onProgress?.(`Uploaded ${uploaded}/${localImages.length}: ${fileName}`);
    } catch (err) {
      console.error(`Error uploading ${localPath}:`, err);
      errors++;
    }
  }

  // Step 2: Update all Firestore product documents
  let updated = 0;
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.PRODUCTS));
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      let changed = false;

      const newImages = (data.images || []).map((img: string) => {
        if (pathToUrl.has(img)) {
          changed = true;
          return pathToUrl.get(img)!;
        }
        return img;
      });

      const newThumb = pathToUrl.get(data.thumbnail) || data.thumbnail;
      if (changed || newThumb !== data.thumbnail) {
        await updateDoc(doc(db, COLLECTIONS.PRODUCTS, docSnap.id), {
          images: newImages,
          thumbnail: newThumb,
          updatedAt: Timestamp.now(),
        });
        updated++;
      }
    }
    onProgress?.(`Updated ${updated} product documents in Firestore`);
  } catch (err) {
    console.error('Error updating Firestore documents:', err);
  }

  return { uploaded, updated, errors };
};

/**
 * Delete all products from Firestore and their images from Storage
 */
export const deleteAllProducts = async (): Promise<number> => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.PRODUCTS));
    
    const deletePromises = snapshot.docs.map(async (docSnapshot) => {
      const product = docSnapshot.data() as Product;
      // Delete product images from storage
      for (const imageUrl of product.images || []) {
        await deleteProductImage(imageUrl);
      }
      // Delete product document
      await deleteDoc(docSnapshot.ref);
    });
    
    await Promise.all(deletePromises);
    return snapshot.size;
  } catch (error) {
    console.error('Error deleting all products:', error);
    throw new Error('Failed to delete all products');
  }
};
